import React, { useRef, useEffect } from 'react';

// Iconos con colores naturales que combinan con la página
const DurationIcon = () => (
  <svg className="w-6 h-6 mr-3 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>
);

const DressCodeIcon = () => (
  <svg className="w-6 h-6 mr-3 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
  </svg>
);

const BenefitIcon = () => (
  <svg className="w-5 h-5 mr-3 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>
);

function EsteticaModal({ isOpen, onClose, title, content, imageUrl, duration, dressCode, benefits }) {
  const modalRef = useRef();

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleReserveClick = () => {
    const whatsappMessage = `¡Hola! Me gustaría reservar el tratamiento "${title}" en Cleo Estética.

📋 Detalles del tratamiento:
• Nombre: ${title}
• Duración: ${duration || 'Consultar'}
• Vestimenta: ${dressCode || 'Ropa cómoda'}

Por favor, me gustaría conocer:
• Horarios disponibles
• Precio del tratamiento
• Requisitos previos

¡Gracias! 🙏`;
    const whatsappUrl = `https://wa.me/+595986523658?text=${encodeURIComponent(whatsappMessage)}`;
    
    alert('¡Redirigiendo a WhatsApp! Se abrirá una nueva ventana para que puedas contactar con Cleo Estética.');
    
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-0 sm:p-4"
        onClick={handleOverlayClick}
        style={{ overflow: 'hidden' }}
      >
        {/* Botón flotante de cerrar para móviles - solo en pantallas muy pequeñas */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-[10000] bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all duration-300 hidden sm:hidden md:flex"
          aria-label="Cerrar modal"
        >
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <div
          ref={modalRef}
          className="bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-2xl w-full max-w-none sm:max-w-sm md:max-w-md lg:max-w-2xl xl:max-w-3xl mx-0 sm:mx-4 md:mx-0 overflow-hidden border-0 sm:border border-white/30 h-full sm:h-auto max-h-none sm:max-h-[90vh] md:max-h-[85vh] lg:max-h-[80vh] flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-3 sm:p-4 md:p-6 lg:p-6 relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-green-50/20 to-transparent"></div>

            <div className="flex justify-between items-center relative z-10">
              {/* Botón de atrás para móviles */}
              <button
                onClick={onClose}
                className="text-white hover:text-green-200 transition-all duration-300 p-2 rounded-full hover:bg-white/20 flex-shrink-0 mr-2 sm:hidden"
                aria-label="Volver atrás"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>

              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white pr-2 sm:pr-4 flex-1 min-w-0">
                {title}
              </h3>

              <button
                onClick={onClose}
                className="text-white hover:text-green-200 transition-all duration-300 p-2 sm:p-2 md:p-3 rounded-full hover:bg-white/20 flex-shrink-0 ml-2 hidden sm:flex"
                aria-label="Cerrar modal"
              >
                <svg className="w-6 h-6 sm:w-6 sm:h-6 md:w-6 md:h-6 lg:w-7 lg:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-3 sm:p-4 md:p-6 lg:p-6 flex-1 overflow-y-auto custom-scrollbar">
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-6">
              {/* Columna izquierda - Imagen y descripción */}
              <div>
                {imageUrl && (
                  <div className="mb-4 sm:mb-6 rounded-lg overflow-hidden shadow-lg border border-white/30 relative">
                    <img
                      src={imageUrl}
                      alt={title}
                      className="w-full h-32 sm:h-32 md:h-48 lg:h-64 object-cover object-center"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                  </div>
                )}

                <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base md:text-base lg:text-lg">
                  {content}
                </p>
              </div>

              {/* Columna derecha - Información del tratamiento */}
              <div className="space-y-3 sm:space-y-4">
                {duration && (
                  <div className="flex items-start p-2.5 sm:p-3 md:p-3 rounded-lg bg-green-50/30 border border-green-200/30">
                    <DurationIcon />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base md:text-base lg:text-lg">Duración</p>
                      <p className="text-gray-600 text-sm sm:text-base md:text-base lg:text-lg">{duration}</p>
                    </div>
                  </div>
                )}

                {dressCode && (
                  <div className="flex items-start p-2.5 sm:p-3 md:p-3 rounded-lg bg-teal-50/30 border border-teal-200/30">
                    <DressCodeIcon />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base md:text-base lg:text-lg">Vestimenta</p>
                      <p className="text-gray-600 text-sm sm:text-base md:text-base lg:text-lg">{dressCode}</p>
                    </div>
                  </div>
                )}

                {benefits && benefits.length > 0 && (
                  <div>
                    <p className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base md:text-base lg:text-lg">Beneficios</p>
                    <ul className="space-y-1.5 sm:space-y-2">
                      {benefits.map((benefit, index) => (
                        <li
                          key={index}
                          className="flex items-start p-2.5 sm:p-3 md:p-3 rounded-lg bg-emerald-50/30 border border-emerald-200/30"
                        >
                          <BenefitIcon />
                          <span className="text-gray-700 text-sm sm:text-base md:text-base lg:text-lg">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer con botón de WhatsApp profesional */}
          <div className="bg-gradient-to-r from-green-50/50 to-emerald-50/50 px-3 sm:px-4 md:px-6 lg:px-6 py-4 sm:py-4 md:py-5 lg:py-6 border-t border-green-200/30 relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-green-50/20 to-transparent"></div>

            <div className="relative z-10">
              <div className="text-center mb-3 sm:mb-4 lg:mb-6">
                <p className="text-sm sm:text-base md:text-base lg:text-lg text-gray-600 mb-1 sm:mb-2">¿Te interesa este tratamiento?</p>
                <p className="text-xs sm:text-sm text-gray-500">Haz clic para reservar tu cita</p>
              </div>

              <button
                onClick={handleReserveClick}
                className="w-full bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 hover:from-green-600 hover:via-emerald-600 hover:to-green-700 text-white font-semibold py-3 sm:py-3 md:py-4 lg:py-4 px-4 sm:px-6 md:px-6 rounded-lg transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
              >
                {/* Icono original de WhatsApp */}
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 sm:mr-3"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>

                <span className="text-sm sm:text-base font-medium">
                  Reservar Cita por WhatsApp
                </span>
              </button>

              <div className="text-center mt-3 sm:mt-3 lg:mt-4">
                <p className="text-xs sm:text-sm text-gray-500">
                  📞 Respuesta inmediata • 💬 Consulta gratuita • ⏰ Horarios flexibles
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos CSS para scrollbar personalizado */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #eef1e9;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c7cfba;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a9b497;
        }
      `}</style>
    </>
  );
}

export default EsteticaModal;
